// these should not be ignored, but because we wrote interactive elements
// without using semantic HTML they are triggering. Rather than rewriting the
// whole thing I am ignoring it temporarily.

/* eslint jsx-a11y/click-events-have-key-events: 0 */
/* eslint jsx-a11y/no-noninteractive-element-interactions: 0 */
/* eslint jsx-a11y/control-has-associated-label: 0 */

import React from "react";
import _ from "underscore";
import classnames from "classnames";
import { connect } from "react-redux";
import { Button, Toggle } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { DropdownButton, MenuItem, OverlayTrigger, Tooltip } from "react-bootstrap";
import Dropzone from "react-dropzone";

import {
  AccountShape,
  AppShape,
  AssetTypes,
  assetTypeToStrMap,
  BorrowerEntity,
  HurdleShape,
  OwnedRealEstate,
  Tradeline,
} from "@eave/shared/types/api";
import Section from "web_modules/shared/Section";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import SumSectionHeading from "web_modules/shared/SectionHeading/SumSectionHeading";
import { formatCurrency } from "@eave/shared/utils/currency";
import AssetSummary from "underwriter_dashboard/hurdle_editors/AssetSummary/AssetSummary";
import BorrowerEntityUtil, {
  borrowerCount,
  borrowersAscending,
  getBorrowerEntityAsString,
} from "@eave/shared/utils/borrowers";

import APIClient from "../../../APIClient";
import Money from "../../../Money";

import { createHurdleOverride, loadApp, updateHurdleOverride } from "../../actions";

import AccountTypeSelector from "./AccountTypeSelector";
import AddAsset from "./AddAsset";
import AccountIdentity from "./AccountIdentity";
import DocsForAccount from "./DocsForAccount";
import RevisableTextField from "./RevisableTextField";
import TxnsForAccount from "./TxnsForAccount";
import { assetTypeIsExoticOrIlliquidAsset, EMDASH, stripSymbols } from "./shared";
import Reserves from "../Reserves";
import LiabilityReserves from "../LiabilityReserves";
import CashToCloseAssetSummary, { computeTotalCashToClose } from "./CashToCloseAssetSummary";
import Summary, { DueFromBorrower } from "./Summary/index";

require(`../../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/\
asset_summary.scss`);

const humanize = require("underscore.string/humanize");

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

const assetSummaryStyles = require("./styles.module.scss");

const PLAID_LOOKBACK_DAYS_OPTIONS = [90, 365];

/* helpers */

const splitExternalCategory = cat => {
  const arr = cat.split(",");

  // https://plaid.com/docs/api/#data-overview

  if (arr.length !== 2) {
    throw new Error(`bad external category ${cat}`);
  }

  return {
    type: arr[0],
    subtype: arr[1],
  };
};

const extCatToTypeStr = cat => {
  const { type } = splitExternalCategory(cat);

  const map = {
    depository: "Deposit",
  };

  return map[type] || humanize(type);
};

const extCatToSubtypeStr = cat => {
  const { subtype } = splitExternalCategory(cat);

  const map = {
    "cash management": "Cash mgmt",
    cd: "CD",
    "certificate of deposit": "CD",
    "credit card": "CC",
    ira: "IRA",
    "line of credit": "LOC",
  };

  return map[subtype] || humanize(subtype);
};

/* AssetSummaryEditor */

interface HurdleEditorProps {
  dispatch?: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class AssetSummaryEditor extends React.Component<HurdleEditorProps, any> {
  static getChecksForAcct(props, acct) {
    const checks: any = {
      category_verification: false,
    };

    if (parseFloat(acct.value) > 0.02 * parseFloat(props.app.computed_properties.purchase_price)) {
      checks.account_ownership = false;
    }
    if (assetTypeIsExoticOrIlliquidAsset(acct.revised_type || acct.type)) {
      checks.document_sufficiency = false;
    }

    return checks;
  }

  static saveAccountsIfNeeded(accountsDiff): any {
    if (accountsDiff.length) {
      return APIClient.put("/accounts_multi_update", {
        accounts: accountsDiff,
      });
    }
    return Promise.resolve();
  }

  static getResolvedAccountType(tradeline: Tradeline) {
    return tradeline.operator_account_type || tradeline.account_type;
  }

  static getLiabilityReserves(app: AppShape): Tradeline[] {
    if (!app.credit_reports || app.credit_reports.length === 0) {
      return [];
    }
    let tradelines = [];
    app.credit_reports.forEach(cr => {
      cr.tradelines.forEach(t => {
        if (AssetSummaryEditor.getResolvedAccountType(t) === "Open") {
          tradelines = [...tradelines, t];
        }
      });
    });
    return tradelines;
  }

  static computeTotalReserves(
    reservesWsTotal: string,
    app: AppShape,
    forBorrowerEntity?: number,
  ): number {
    const liabilityReservesTotal = AssetSummaryEditor.getLiabilityReserves(app).reduce(
      (acc, liabilityReserve) => {
        const { borrower_entity, current_balance_amount } = liabilityReserve;
        if (Number.isNaN(current_balance_amount)) {
          return acc;
        }

        // Filter by forBorrowerEntity if provided
        if (forBorrowerEntity && forBorrowerEntity !== borrower_entity) {
          return acc;
        }
        return acc + parseFloat(current_balance_amount);
      },
      0,
    );

    return Number(reservesWsTotal) + Number(liabilityReservesTotal);
  }

  constructor(props) {
    super(props);
    const { hurdle } = props;

    // TODO: jjw: now that we've added enough state, we should restructure this
    // so it's a bit flatter (i.e., keep one array of accounts and their diffs)

    const ignored = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, acct.ignored])
      .object()
      .value();

    const forReservesOnly = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, acct.for_reserves_only])
      .object()
      .value();

    const ignoredPsps = _.chain(props.app.owned_real_estate)
      .map((psp: any) => [psp.id, !psp.use_proceeds_as_asset])
      .object()
      .value();

    const borrowerEntityPsps = props.app.owned_real_estate.reduce(
      (acc: object, psp: OwnedRealEstate) => ({ ...acc, [psp.id]: psp.borrower_entity }),
      {},
    );

    const borrowerEntity = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [
        acct.id,
        acct.borrower_entity === null ? "unknown" : acct.borrower_entity,
      ])
      .object()
      .value();

    const revisedTypes = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, acct.revised_type])
      .object()
      .value();

    const numberSuffixRevised = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, acct.number_suffix_revised])
      .object()
      .value();
    const institutionRevised = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, acct.institution_revised])
      .object()
      .value();
    const valueRevised = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, acct.value_revised])
      .object()
      .value();
    const discountFactorRevised = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, Number(acct.discount_factor_revised) * 100])
      .object()
      .value();
    const jointPercentRevised = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, Number(acct.joint_percent_revised) * 100])
      .object()
      .value();

    const checks = _.chain(props.app.accounts)
      .map((acct: AccountShape) => [acct.id, AssetSummaryEditor.getChecksForAcct(props, acct)])
      .object()
      .value();

    if (hurdle.override && hurdle.override.values && hurdle.override.values.accounts) {
      _.extend(
        checks,
        _.chain(hurdle.override.values.accounts)
          .map((acctState: any) => [acctState.id, acctState.checks])
          .object()
          .value(),
      );
    }

    this.state = {
      ignored,
      ignoredPsps,
      borrowerEntityPsps,
      borrowerEntity,
      revisedTypes,
      numberSuffixRevised,
      institutionRevised,
      valueRevised,
      discountFactorRevised,
      jointPercentRevised,
      checks,
      forReservesOnly,
      showingTxnsForAcctId: null,
      showingDocsForAcctId: null,
      showingChecksForAcctId: null,
      showAll: false,
      accountIdentity: null,

      // The values below are for the loan, they're NOT a per-borrower value
      sellerCredits: null,
      otherCredits: null,
      cashToCloseTotal: null,
      reservesTotal: null,
      totalClosingCosts: null,
      closingCostsPaidBeforeClosing: null,

      showSummary: false,
    };
  }

  onSave = () => {
    const { app } = this.props;
    const {
      ignored,
      forReservesOnly,
      borrowerEntity,
      revisedTypes,
      numberSuffixRevised,
      institutionRevised,
      valueRevised,
      discountFactorRevised,
      jointPercentRevised,
    } = this.state;

    this.savePsps();

    // TODO: jjw: this function is gross; would be nice if we had APIClient
    // return a deferred and then we could chain together one or more ops

    const accountsDiff = [];

    _.each(app.accounts, (acct: AccountShape) => {
      const updatedIgnored = ignored[acct.id];
      const updatedReserves = forReservesOnly[acct.id];
      const updatedBorrowerEntity =
        borrowerEntity[acct.id] === "unknown" ? null : borrowerEntity[acct.id];
      const updatedRevisedType = revisedTypes[acct.id];
      const updatedNumberSuffixRevised = numberSuffixRevised[acct.id];
      const updatedInstitutionRevised = institutionRevised[acct.id];
      const updatedValueRevised = valueRevised[acct.id];
      const updatedDiscountFactorRevised = discountFactorRevised[acct.id];
      const updatedJointPercentRevised = jointPercentRevised[acct.id];

      const diff: any = {
        id: acct.id,
      };

      if (acct.ignored !== updatedIgnored) {
        diff.ignored = updatedIgnored;
      }
      if (acct.for_reserves_only !== updatedReserves) {
        diff.for_reserves_only = updatedReserves;
      }
      if (acct.borrower_entity !== updatedBorrowerEntity) {
        diff.borrower_entity = updatedBorrowerEntity;
      }
      if (acct.revised_type !== updatedRevisedType) {
        diff.revised_type = updatedRevisedType;
      }
      if (acct.number_suffix_revised !== updatedNumberSuffixRevised) {
        diff.number_suffix_revised = updatedNumberSuffixRevised;
      }
      if (acct.institution_revised !== updatedInstitutionRevised) {
        diff.institution_revised = updatedInstitutionRevised;
      }
      if (acct.value_revised !== updatedValueRevised) {
        diff.value_revised = updatedValueRevised;
      }
      if (acct.discount_factor_revised !== updatedDiscountFactorRevised) {
        diff.discount_factor_revised = updatedDiscountFactorRevised / 100;
      }
      if (acct.joint_percent_revised !== updatedJointPercentRevised) {
        diff.joint_percent_revised = updatedJointPercentRevised / 100;
      }

      if (Object.keys(diff).length > 1) {
        accountsDiff.push(diff); // push only if more than id set
      }
    });

    AssetSummaryEditor.saveAccountsIfNeeded(accountsDiff).then(() => {
      this.saveOverride();
    });
  };

  getDocsForAcct(acctId) {
    const {
      app: { documents },
    } = this.props;

    return _.filter(
      documents,
      (d: any) => d.owner_entity_type === "account" && d.owner_entity_id === acctId,
    );
  }

  getValue(acct: AccountShape) {
    const { valueRevised } = this.state;
    const revised = valueRevised[acct.id];

    if (revised) {
      return revised;
    }
    if (revised === null && acct.value_revised) {
      return acct.value_original;
    }
    if (acct.value_revised) {
      return acct.value_revised;
    }

    return acct.value_original;
  }

  /**
   * Returns the assets total for a borrower
   * @param {BorrowerEntity} borrowerEntity is the entity for the borrower for which you are getting the assets
   * @param {boolean} asFormattedString When true, returns a formatted currency string
   * @param {ownedRealEstate} ownedRealEstate the real estate in which you'd like to search for the borrowers ownership
   * @param {accounts} accounts the accounts you'd like to check for the borrowers ownership
   */
  getAssetsForBorrower(
    borrowerEntity: BorrowerEntity,
    ownedRealEstate: OwnedRealEstate[],
    accounts: AccountShape[],
  ): string | number {
    const { showAll, ignoredPsps, ignored: ignoredAccts } = this.state;
    const pendingSaleProperties = AssetSummary.getPendingSaleProperties(
      borrowerEntity,
      ownedRealEstate,
    );
    const sortedAccounts = AssetSummary.getSortedAccounts(borrowerEntity, accounts, showAll);

    const pspTotal = pendingSaleProperties.reduce((acc: number, psp: OwnedRealEstate) => {
      const ignored = ignoredPsps[psp.id];
      const { usable_net_sale_proceeds } = psp;
      if (!ignored) {
        return acc + Number(usable_net_sale_proceeds);
      }
      return acc;
    }, 0);

    const accountsTotal = sortedAccounts.reduce((acc, account: any) => {
      const ignored = ignoredAccts[account.id];
      if (!ignored) {
        return acc + Number(account.value);
      }
      return acc;
    }, 0);

    const total = pspTotal + accountsTotal;

    return total;
  }

  showDocsForAcct = acctId => {
    this.setState({ showingDocsForAcctId: acctId });
  };

  hideModals = () => {
    this.setState({
      showingTxnsForAcctId: null,
      showingDocsForAcctId: null,
      showingChecksForAcctId: null,
      showingAddAsset: false,
      showingAccountIdentity: false,
      accountIdentity: null,
    });
  };

  showAddAsset = () => {
    this.setState({ showingAddAsset: true });
  };

  showTxnsForAcct = acctId => {
    this.setState({ showingTxnsForAcctId: acctId });
  };

  importTransactions = (acctId, files) => {
    if (!files || !files.length) return;

    if (files.length > 1) {
      alert("You can only import one CSV file at a time.");
      return;
    }

    const { app, dispatch } = this.props;

    APIClient.dropzoneUpload(
      `/accounts/${acctId}/csv_transactions_import`,
      "transactionsfile",
      files[0],
      {},
    ).then(() => {
      loadApp(dispatch, app.id);
    });
  };

  toggleIgnoreForPSP = pspId => {
    const { ignoredPsps } = this.state;

    if (!ignoredPsps.hasOwnProperty(pspId)) return;

    this.setState(state => ({
      ...state,
      ignoredPsps: {
        ...ignoredPsps,
        [pspId]: !ignoredPsps[pspId],
      },
    }));
  };

  toggleIgnoreForAcct = acctId => {
    const { ignored } = this.state;

    if (!ignored.hasOwnProperty(acctId)) return;

    this.setState(state => ({
      ...state,
      ignored: {
        ...ignored,
        [acctId]: !ignored[acctId],
      },
    }));
  };

  changeRevisedTypeForAcct = (acct, e) => {
    const { revisedTypes, checks } = this.state;

    if (!revisedTypes.hasOwnProperty(acct.id)) return;

    const t = e ? e.value : null;

    this.setState(state => ({
      ...state,
      revisedTypes: {
        ...revisedTypes,
        [acct.id]: t,
      },
      checks: {
        ...checks,
        [acct.id]: AssetSummaryEditor.getChecksForAcct(this.props, {
          ...acct,
          revised_type: t,
        }),
      },
    }));
  };

  // TODO: jjw: refactor these three to use the same func

  changeNumberSuffixRevisedForAcct = (acct, value: string) => {
    const { numberSuffixRevised } = this.state;

    if (!numberSuffixRevised.hasOwnProperty(acct.id)) return;

    const str = value.trim();

    this.setState(state => ({
      ...state,
      numberSuffixRevised: {
        ...numberSuffixRevised,
        [acct.id]: str === "" ? null : str,
      },
    }));
  };

  changeInstitutionRevisedForAcct = (acct, value) => {
    const { institutionRevised } = this.state;

    if (!institutionRevised.hasOwnProperty(acct.id)) return;

    this.setState(state => ({
      ...state,
      institutionRevised: {
        ...institutionRevised,
        [acct.id]: value === "" ? null : value,
      },
    }));
  };

  changeValueRevisedForAcct = (acct, value: string) => {
    const { valueRevised } = this.state;

    if (!valueRevised.hasOwnProperty(acct.id)) return;

    // Remove whitespace, commas, currency symbols
    const str = stripSymbols(value);

    let val;
    if (str === "" || parseFloat(str) === 0) {
      val = null;
    } else if (str.match(/^-?\d*\.?\d{0,2}$/)) {
      val = str;
    } else {
      return;
    }

    this.setState(state => ({
      ...state,
      valueRevised: {
        ...valueRevised,
        [acct.id]: val,
      },
    }));
  };

  changeDiscountFactorRevisedForAcct = (acct, value) => {
    const { discountFactorRevised } = this.state;

    if (!discountFactorRevised.hasOwnProperty(acct.id)) return;

    let val;
    if (value === "" || parseFloat(value) === 0) {
      val = null;
    } else if (value.match(/^[1-9][0-9]?$|^100$/)) {
      val = value;
    } else {
      return;
    }

    this.setState(state => ({
      ...state,
      discountFactorRevised: {
        ...discountFactorRevised,
        [acct.id]: val,
      },
    }));
  };

  dueFromBorrowerFromApp = (app: AppShape): DueFromBorrower => {
    const { totalClosingCosts, closingCostsPaidBeforeClosing } = this.state;

    return {
      salesContractPrice: app.purchase_contract?.salesPriceTotalAmount
        ? Number(app.purchase_contract?.salesPriceTotalAmount)
        : 0,
      improvementsRenovationsRepairs: 0, // not currently used
      land: 0, // not currently used
      mortgagePayoff: 0, // for refinance
      creditCardsOther: Number(closingCostsPaidBeforeClosing),
      borrowerClosingCosts: totalClosingCosts ? Number(totalClosingCosts) : 0,
      discountPoints: Number(app.decision.discount_points),
    };
  };

  toggleReservesOnlyForAcct = acctId => {
    const { forReservesOnly } = this.state;

    if (!forReservesOnly.hasOwnProperty(acctId)) return;

    this.setState(state => ({
      ...state,
      forReservesOnly: {
        ...forReservesOnly,
        [acctId]: !forReservesOnly[acctId],
      },
    }));
  };

  showSummaryButton = () => {
    const { showSummary } = this.state;

    return (
      <div className={assetSummaryStyles.flexRight}>
        <Button
          className={assetSummaryStyles.summaryButton}
          onClick={() => {
            this.setState(state => {
              const { showSummary: s } = state;

              return {
                showSummary: !s,
              };
            });
          }}
        >
          {showSummary ? "Hide" : "Show"} Summary
        </Button>
      </div>
    );
  };

  refreshTransactions(accountId: string, days: number) {
    if (
      !confirm(
        `This will pull all transactions from ${days} days since the account was ` +
          "created without overwriting existing transactions.",
      )
    ) {
      return;
    }

    const { app, dispatch } = this.props;

    APIClient.put(`/uw/accounts/${accountId}/transactions`, { days }).then(() => {
      loadApp(dispatch, app.id);
    });
  }

  showAccountIdentity(account: AccountShape | OwnedRealEstate) {
    this.setState({ accountIdentity: account });
  }

  changeJointPercentRevisedForAcct(acct, value) {
    const { jointPercentRevised } = this.state;

    if (!jointPercentRevised.hasOwnProperty(acct.id)) return;

    let val;
    if (value === "" || parseFloat(value) === 0) {
      val = null;
    } else if (value.match(/^[1-9][0-9]?$|^100$/)) {
      val = value;
    } else {
      return;
    }

    this.setState(state => ({
      ...state,
      jointPercentRevised: {
        ...jointPercentRevised,
        [acct.id]: val,
      },
    }));
  }

  toggleCheckForAcct(acctId, chkType) {
    const { checks } = this.state;

    if (!checks.hasOwnProperty(acctId)) return;
    if (!checks[acctId].hasOwnProperty(chkType)) return;

    this.setState(state => ({
      ...state,
      checks: {
        ...checks,
        [acctId]: {
          ...checks[acctId],
          [chkType]: !checks[acctId][chkType],
        },
      },
    }));
  }

  saveOverride() {
    const { dispatch, app, hurdle } = this.props;
    const { checks } = this.state;

    const checkState = [];
    _.each(checks, (c, id: string) => {
      checkState.push({
        checks: c,
        id: parseInt(id, 10), // need to cast since used as obj key in state
      });
    });

    if (!hurdle.override) {
      createHurdleOverride(dispatch, {
        appId: app.id,
        hurdleName: hurdle.name,
        hurdleOverrideValues: { accounts: checkState },
      });
    } else {
      updateHurdleOverride(dispatch, {
        appId: app.id,
        hurdleOverrideId: hurdle.override.id,
        hurdleOverrideValues: { accounts: checkState },
      });
    }

    loadApp(dispatch, app.id);
  }

  savePsps() {
    const {
      app: { owned_real_estate },
    } = this.props;
    const { ignoredPsps, borrowerEntityPsps } = this.state;

    APIClient.put("/uw/owned_real_estate", {
      reos: owned_real_estate.map((psp: OwnedRealEstate) => {
        const updatedIgnored = ignoredPsps[psp.id];
        const existingIgnored = !psp.use_proceeds_as_asset;

        let newPSP = psp;
        if (existingIgnored !== updatedIgnored) {
          newPSP = { ...newPSP, use_proceeds_as_asset: !updatedIgnored };
        }

        if (borrowerEntityPsps[psp.id] !== psp.borrower_entity) {
          newPSP = { ...newPSP, borrower_entity: borrowerEntityPsps[newPSP.id] };
        }

        return newPSP;
      }),
    });
  }

  renderAddAssetModal() {
    const {
      app: { id },
      dispatch,
    } = this.props;
    const { showingAddAsset } = this.state;

    if (!showingAddAsset) {
      return null;
    }

    return (
      <AddAsset
        appId={id}
        dispatch={dispatch}
        onHide={() => {
          this.hideModals();
        }}
      />
    );
  }

  renderTable(borrowerEntity: BorrowerEntity) {
    const {
      showAll,
      ignoredPsps,
      ignored: ignoredAccts,
      forReservesOnly,
      institutionRevised: institutionRevisedAccts,
      numberSuffixRevised: numberSuffixRevisedAccts,
      revisedTypes,
      valueRevised: valueRevisedAccts,
      discountFactorRevised: discountFactorRevisedAccts,
    } = this.state;
    const { app } = this.props;
    const pendingSaleProperties = AssetSummary.getPendingSaleProperties(
      borrowerEntity,
      app.owned_real_estate,
    );
    const sortedAccounts = AssetSummary.getSortedAccounts(borrowerEntity, app.accounts, showAll);

    return (
      <div>
        <div className={assetSummaryStyles.assetsShowAllToggle}>
          <h4>Accounts and Sale Proceeds</h4>
          <div className={assetSummaryStyles.assetsShowAllToggleContainer}>
            <Toggle
              toggled={showAll}
              onToggle={() => this.setState({ showAll: !showAll })}
              labelA="Hiding ignored assets"
              labelB="Showing ignored assets"
            />
          </div>
        </div>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th className="text-center">Include</th>
              <th className="text-center">Only for Reserves</th>
              <th className="text-center">Identity</th>
              <th>Institution</th>
              <th>Account number</th>
              <th>External type</th>
              <th>Type</th>
              <th className="text-right">Total</th>
              <th className="text-right">Factor</th>
              <th className="text-right">Usable</th>
              <th className="text-right">Txns</th>
              <th className="text-center">Stmts</th>
              <th className="text-center">Refresh Transactions</th>
            </tr>
          </thead>
          <tbody>
            {pendingSaleProperties.map((psp: OwnedRealEstate) => {
              const ignored = ignoredPsps[psp.id];
              const institution = "Property sale";
              const value = psp.usable_net_sale_proceeds;

              const rowClasses = classnames({
                "asset-summary__row": true,
                "asset-summary__row--ignored": ignored,
              });

              const useClasses = classnames({
                fa: true,
                "fa-square-o": ignored,
                "fa-check-square-o": !ignored,
              });

              const identityClasses = classnames({
                fa: true,
                "fa-user": !ignored,
              });

              return (
                <tr key={psp.id} className={rowClasses}>
                  <td
                    className="asset-summary__clickable text-center"
                    onClick={() => {
                      this.toggleIgnoreForPSP(psp.id);
                    }}
                  >
                    <i className={useClasses} />
                  </td>
                  <td className="text-center">{EMDASH}</td>
                  <td
                    className="asset-summary__clickable text-center"
                    onClick={() => {
                      this.showAccountIdentity(psp);
                    }}
                  >
                    <button className={identityClasses} type="button" />
                  </td>
                  <td className="text-center">{EMDASH}</td>
                  <td>{institution}</td>
                  <td className="text-center">{EMDASH}</td>
                  <td>{assetTypeToStrMap[AssetTypes.NetEquity]}</td>
                  <td className="asset-summary__strikable text-right">
                    <Money value={Number(value)} />
                  </td>
                  <td>100%</td>
                  <td className="asset-summary__strikable text-right">
                    <Money value={Number(value)} />
                  </td>
                  <td className="text-center">{EMDASH}</td>
                  <td className="text-center">{EMDASH}</td>
                  <td />
                </tr>
              );
            })}
            {sortedAccounts.map((acct: any) => {
              const ignored = ignoredAccts[acct.id];
              const forReserves = forReservesOnly[acct.id];
              const institutionRevised = institutionRevisedAccts[acct.id];
              const numberSuffixRevised = numberSuffixRevisedAccts[acct.id];
              const revisedType = revisedTypes[acct.id];
              const valueRevised = valueRevisedAccts[acct.id];
              const discountFactorRevised = discountFactorRevisedAccts[acct.id];

              const rowClasses = classnames({
                "asset-summary__row": true,
                "asset-summary__row--ignored": ignored,
              });
              const useClasses = classnames({
                fa: true,
                "fa-square-o": ignored,
                "fa-check-square-o": !ignored,
              });
              const reservesClasses = classnames({
                fa: true,
                "fa-square-o": !forReserves,
                "fa-check-square-o": forReserves,
              });
              const identityClasses = classnames({
                fa: true,
                "fa-user": !ignored,
              });

              return (
                <tr key={acct.id} className={rowClasses}>
                  <td
                    className="asset-summary__clickable text-center"
                    onClick={() => {
                      this.toggleIgnoreForAcct(acct.id);
                    }}
                  >
                    <i className={useClasses} />
                  </td>
                  <td
                    className="asset-summary__clickable text-center"
                    onClick={() => {
                      this.toggleReservesOnlyForAcct(acct.id);
                    }}
                  >
                    <i className={reservesClasses} />
                  </td>
                  <td
                    className="asset-summary__clickable text-center"
                    onClick={() => {
                      this.showAccountIdentity(acct);
                    }}
                  >
                    <button className={identityClasses} type="button" />
                  </td>
                  <td>
                    <RevisableTextField
                      width={100}
                      original={acct.institution_original}
                      revised={institutionRevised}
                      ignored={ignored}
                      onUpdate={value => {
                        this.changeInstitutionRevisedForAcct(acct, value);
                      }}
                    />
                  </td>
                  <td>
                    <RevisableTextField
                      width={50}
                      addEllipsis
                      original={acct.number_suffix_original}
                      revised={numberSuffixRevised}
                      ignored={ignored}
                      onUpdate={value => {
                        this.changeNumberSuffixRevisedForAcct(acct, value);
                      }}
                    />
                  </td>
                  {acct.external_category ? (
                    <td>
                      {extCatToTypeStr(acct.external_category)}
                      <br />/ {extCatToSubtypeStr(acct.external_category)}
                    </td>
                  ) : (
                    <td className="text-center">
                      {EMDASH}
                      <br />
                      &nbsp;
                    </td>
                  )}
                  <td>
                    <AccountTypeSelector
                      type={acct.type}
                      revisedType={revisedType}
                      ignored={ignored}
                      onUpdate={e => {
                        this.changeRevisedTypeForAcct(acct, e);
                      }}
                    />
                  </td>
                  <td className="asset-summary__strikable text-right">
                    <RevisableTextField
                      width={100}
                      addDollarSign
                      original={acct.value_original}
                      revised={valueRevised}
                      ignored={ignored}
                      onUpdate={value => {
                        this.changeValueRevisedForAcct(acct, value);
                      }}
                    />
                  </td>
                  <td className="asset-summary__strikable text-right">
                    <RevisableTextField
                      width={50}
                      addPercentageSign
                      original={String(acct.discount_factor_original * 100)}
                      revised={discountFactorRevised || null}
                      ignored={ignored}
                      onUpdate={value => {
                        this.changeDiscountFactorRevisedForAcct(acct, value);
                      }}
                    />
                  </td>
                  <td className="text-right">
                    {ignored ? EMDASH : <Money value={acct.usable_value} />}
                  </td>
                  {acct.transactions.length ? (
                    <td className="asset-summary__clickable text-right">
                      <Button
                        kind="tertiary"
                        onClick={() => {
                          this.showTxnsForAcct(acct.id);
                        }}
                        style={{ justifyContent: "center", width: "30px", padding: "5px" }}
                      >
                        {acct.transactions.length}
                      </Button>
                    </td>
                  ) : (
                    <td className="text-right">
                      <Dropzone
                        onDrop={files => {
                          this.importTransactions(acct.id, files);
                        }}
                        className="asset-summary__dropzone"
                        activeClassName="active"
                      >
                        <div className="asset-summary__clickable">0</div>
                      </Dropzone>
                    </td>
                  )}
                  {this.getDocsForAcct(acct.id).length ? (
                    <td
                      className="asset-summary__clickable text-center"
                      onClick={() => {
                        this.showDocsForAcct(acct.id);
                      }}
                    >
                      <i className="fa fa-file-text-o" />
                    </td>
                  ) : (
                    <td className="text-center">{EMDASH}</td>
                  )}
                  <td>
                    {acct.external_id && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip">
                            Use this button to refresh all account transactions for this account.
                          </Tooltip>
                        }
                      >
                        <DropdownButton
                          id="institution_dropdown"
                          bsSize="small"
                          key="institution dropdown"
                          style={{ marginLeft: 10 }}
                          title="Refresh"
                        >
                          {PLAID_LOOKBACK_DAYS_OPTIONS.map(days => (
                            <MenuItem
                              key={days}
                              onClick={() => {
                                this.refreshTransactions(acct.id, days);
                              }}
                            >
                              {days} days
                            </MenuItem>
                          ))}
                        </DropdownButton>
                      </OverlayTrigger>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderSummary() {
    const { app, dispatch, hurdle, onHide } = this.props;
    const {
      showingTxnsForAcctId,
      showingDocsForAcctId,
      accountIdentity,
      cashToCloseTotal,
      reservesTotal,
      sellerCredits,
      otherCredits,
      showSummary,
    } = this.state;

    // TODO: jjw: clean these up since they're mutually exclusive states
    let showingTxnsForAcct;
    if (showingTxnsForAcctId !== null) {
      showingTxnsForAcct = _.findWhere(app.accounts, {
        id: showingTxnsForAcctId,
      });
    }
    let showingDocsForAcct;
    if (showingDocsForAcctId !== null) {
      showingDocsForAcct = _.findWhere(app.accounts, {
        id: showingDocsForAcctId,
      });
    }

    const borrower1Assets = this.getAssetsForBorrower(
      BorrowerEntity.Borrower1,
      app.owned_real_estate,
      app.accounts,
    ) as number;
    const borrower2Assets = this.getAssetsForBorrower(
      BorrowerEntity.Borrower2,
      app.owned_real_estate,
      app.accounts,
    ) as number;
    const borrower3Assets = this.getAssetsForBorrower(
      BorrowerEntity.Borrower3,
      app.owned_real_estate,
      app.accounts,
    ) as number;
    const borrower4Assets = this.getAssetsForBorrower(
      BorrowerEntity.Borrower4,
      app.owned_real_estate,
      app.accounts,
    ) as number;
    const addends = AssetSummary.getAddends(
      [borrower1Assets, borrower2Assets, borrower3Assets, borrower4Assets],
      app.borrowers,
    );

    const borrowersReserves = borrowersAscending(app.borrowers).map((b, idx) => {
      return (
        <Section
          key={b.borrower_entity}
          heading={`${getBorrowerEntityAsString(b.borrower_entity)}: Reserves`}
          isLast={idx === borrowerCount(app.borrowers) - 1}
        >
          <Reserves
            app={app}
            borrowerEntity={b.borrower_entity}
            loadApp={loadApp}
            dispatch={dispatch}
            setReserveTotal={rTotal => this.setState({ reservesTotal: rTotal })}
          />
          <div style={{ marginBottom: "10px" }} />
          <LiabilityReserves
            borrowerEntity={b.borrower_entity}
            liabilityReserves={AssetSummaryEditor.getLiabilityReserves(app).filter(
              liabilityReserve => {
                return AssetSummary.isLowestEntity(
                  new BorrowerEntityUtil(liabilityReserve.borrower_entity),
                  b.borrower_entity,
                );
              },
            )}
          />
        </Section>
      );
    });

    return (
      <>
        <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
          {this.showSummaryButton()}

          {showSummary ? (
            <>
              <Summary
                dueFromBorrower={this.dueFromBorrowerFromApp(app)}
                totalMortgageLoans={{
                  loanAmount: {
                    amount: Number(app.computed_properties.loan_amount),
                    withoutInsurance: Number(app.computed_properties.loan_amount_base),
                    insurance:
                      Number(app.computed_properties.loan_amount) -
                      Number(app.computed_properties.loan_amount_base),
                  },
                  otherLoans: 0,
                }}
                totalCredits={{
                  sellerCredits: sellerCredits ? Math.abs(parseFloat(sellerCredits)) : 0,
                  otherCredits: otherCredits ? parseFloat(otherCredits) : 0,
                }}
              />
              {this.showSummaryButton()}
            </>
          ) : null}

          <div className="asset-summary asset-summary__modal-body">
            <SumSectionHeading
              sectionLabel="Asset Details"
              value={parseFloat(app.decision.available_funds)}
              description="Total Usable Assets"
              addends={addends}
            />
            {/* For Borrower 1 */}
            <Section
              heading={`Borrower 1 Assets: ${formatCurrency(borrower1Assets, 2, 2)}`}
              isLast={!app.has_borrower2}
            >
              {this.renderTable(BorrowerEntity.Borrower1)}
            </Section>

            {/* For Borrower 2 */}
            {app.borrowers[BorrowerEntity.Borrower2] && (
              <Section
                heading={`Borrower  2 Assets: ${formatCurrency(borrower2Assets, 2, 2)}`}
                isLast
              >
                {this.renderTable(BorrowerEntity.Borrower2)}
              </Section>
            )}
            {/* For Borrower 3 */}
            {app.borrowers[BorrowerEntity.Borrower3] && (
              <Section
                heading={`Borrower  3 Assets: ${formatCurrency(borrower3Assets, 3, 3)}`}
                isLast
              >
                {this.renderTable(BorrowerEntity.Borrower3)}
              </Section>
            )}
            {/* For Borrower 4 */}
            {app.borrowers[BorrowerEntity.Borrower4] && (
              <Section
                heading={`Borrower  4 Assets: ${formatCurrency(borrower4Assets, 4, 4)}`}
                isLast
              >
                {this.renderTable(BorrowerEntity.Borrower4)}
              </Section>
            )}
            <div style={{ margin: "10px" }}>
              <Button
                key="add asset"
                onClick={() => {
                  this.showAddAsset();
                }}
              >
                Add asset
              </Button>
            </div>
            {accountIdentity && (
              <AccountIdentity
                account={accountIdentity}
                onHide={() => {
                  this.hideModals();
                }}
                dispatch={dispatch}
                appId={app.id}
                hasCoBorrower={!!this.props.app.borrower2_first_name}
              />
            )}
            <SumSectionHeading
              sectionLabel="Assets Required"
              value={
                Number(cashToCloseTotal) +
                AssetSummaryEditor.computeTotalReserves(reservesTotal, app)
              }
              description="Total Assets Required To Close"
              addends={[
                {
                  description: "Cash to Close",
                  value: Number(cashToCloseTotal),
                },
                {
                  description: "Total Reserves",
                  value: AssetSummaryEditor.computeTotalReserves(reservesTotal, app),
                },
              ]}
            />
            <CashToCloseAssetSummary
              appId={app.id}
              setCashToCloseValues={ctc => {
                this.setState({
                  cashToCloseTotal: computeTotalCashToClose(ctc),
                  totalClosingCosts: ctc.total_closing_costs,
                  sellerCredits: ctc.seller_credits,
                  otherCredits: ctc.adjustments_and_other_credits,
                  closingCostsPaidBeforeClosing: ctc.closing_costs_paid_before_closing,
                });
              }}
            />
            {borrowersReserves}
          </div>
          <footer className={styles.footer}>
            <Button kind="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button
              kind="primary"
              onClick={() => {
                this.onSave();
              }}
              renderIcon={Save16}
            >
              Save changes
            </Button>
          </footer>
        </HurdleView>
        {showingTxnsForAcct && (
          <TxnsForAccount
            account={showingTxnsForAcct}
            onHide={() => {
              this.hideModals();
            }}
          />
        )}
        {showingDocsForAcct && (
          <DocsForAccount
            account={showingDocsForAcct}
            docs={this.getDocsForAcct(showingDocsForAcctId)}
            onHide={() => {
              this.hideModals();
            }}
          />
        )}
        {this.renderAddAssetModal()}
      </>
    );
  }

  render() {
    return this.renderSummary();
  }
}

export default connect()(AssetSummaryEditor);
